import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';

interface DialogState {
  isOpen: boolean;
  data?: any;
}

interface DialogContextValue {
  dialogs: Record<string, DialogState>;
  openDialog: (dialogId: string, data?: any) => void;
  closeDialog: (dialogId: string) => void;
  toggleDialog: (dialogId: string, data?: any) => void;
  isDialogOpen: (dialogId: string) => boolean;
  getDialogData: <T = any>(dialogId: string) => T | undefined;
}

interface BoundDialogActions<T = any> {
  isOpen: boolean;
  data: T | undefined;
  open: (data?: T) => void;
  close: () => void;
  toggle: (data?: T) => void;
}

const DialogContext = createContext<DialogContextValue | null>(null);

interface DialogProviderProps {
  children: ReactNode;
}

export function DialogProvider({ children }: DialogProviderProps) {
  const [dialogs, setDialogs] = useState<Record<string, DialogState>>({});

  const openDialog = useCallback((dialogId: string, data?: any) => {
    setDialogs((prev) => ({
      ...prev,
      [dialogId]: { isOpen: true, data },
    }));
  }, []);

  const closeDialog = useCallback((dialogId: string) => {
    setDialogs((prev) => ({
      ...prev,
      [dialogId]: { isOpen: false, data: prev[dialogId]?.data },
    }));
  }, []);

  const toggleDialog = useCallback((dialogId: string, data?: any) => {
    setDialogs((prev) => {
      const current = prev[dialogId];
      const isOpen = current?.isOpen ?? false;
      return {
        ...prev,
        [dialogId]: {
          isOpen: !isOpen,
          data: isOpen ? current?.data : data,
        },
      };
    });
  }, []);

  const isDialogOpen = useCallback(
    (dialogId: string) => dialogs[dialogId]?.isOpen ?? false,
    [dialogs]
  );

  const getDialogData = useCallback(
    <T = any,>(dialogId: string): T | undefined => dialogs[dialogId]?.data as T,
    [dialogs]
  );

  const value = useMemo(
    () => ({
      dialogs,
      openDialog,
      closeDialog,
      toggleDialog,
      isDialogOpen,
      getDialogData,
    }),
    [dialogs, openDialog, closeDialog, toggleDialog, isDialogOpen, getDialogData]
  );

  return <DialogContext.Provider value={value}>{children}</DialogContext.Provider>;
}

export function useDialog<T = any>(dialogId: string): BoundDialogActions<T>;
export function useDialog(): DialogContextValue;
export function useDialog<T = any>(dialogId?: string): BoundDialogActions<T> | DialogContextValue {
  const context = useContext(DialogContext);

  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }

  if (dialogId) {
    return {
      isOpen: context.isDialogOpen(dialogId),
      data: context.getDialogData<T>(dialogId),
      open: (data?: T) => context.openDialog(dialogId, data),
      close: () => context.closeDialog(dialogId),
      toggle: (data?: T) => context.toggleDialog(dialogId, data),
    };
  }

  return context;
}

export const DialogIds = {
  // ── Common ────────────────────────────────────────────────────────
  DELETE_CONFIRM: 'delete-confirm',

  // ── Admin – Users ─────────────────────────────────────────────────
  CREATE_USER: 'create-user',
  EDIT_USER: 'edit-user',
  USER_ADD_ROLE: 'user-add-role',
  USER_NEW_PASSWORD: 'user-new-password',

  // ── Admin – Tracks ────────────────────────────────────────────────
  CREATE_TRACK: 'create-track',
  EDIT_TRACK: 'edit-track',
  TRACK_ASSIGN_INSTRUCTOR: 'track-assign-instructor',
  TRACK_ASSIGN_MENTOR: 'track-assign-mentor',

  // ── Admin – Branches ─────────────────────────────────────────────
  CREATE_BRANCH: 'branch-create',
  EDIT_BRANCH: 'branch-edit',
  DELETE_BRANCH: 'branch-delete',

  // ── Admin – Branch Schedule ───────────────────────────────────────
  BRANCH_SCHEDULE_ADD: 'branch-schedule-add',
  BRANCH_SCHEDULE_EDIT: 'branch-schedule-edit',
  BRANCH_SCHEDULE_DELETE: 'branch-schedule-delete',

  // ── Admin – Online Accounts ───────────────────────────────────────
  CREATE_ONLINE_ACCOUNT: 'online-account-create',
  EDIT_ONLINE_ACCOUNT: 'online-account-edit',
  ONLINE_ACCOUNT_ASSIGN_GROUP: 'online-account-assign-group',

  // ── Admin – Programs ──────────────────────────────────────────────
  CREATE_PROGRAM: 'program-create',
  EDIT_PROGRAM: 'program-edit',
  DELETE_PROGRAM: 'program-delete',

  // ── Admin – Labels ────────────────────────────────────────────────
  CREATE_LABEL: 'label-create',
  EDIT_LABEL: 'label-edit',
  DELETE_LABEL: 'label-delete',

  // ── Admin – Cycles ────────────────────────────────────────────────
  CREATE_CYCLE: 'cycle-create',
  EDIT_CYCLE: 'cycle-edit',
  DELETE_CYCLE: 'cycle-delete',

  // ── Superadmin – Users ────────────────────────────────────────────
  CREATE_SUPERADMIN_USER: 'user-create',
  EDIT_SUPERADMIN_USER: 'user-edit',
  DELETE_SUPERADMIN_USER: 'user-delete',

  // ── Accountant – Chart of Accounts ────────────────────────────────
  CREATE_ACCOUNT: 'create-account',
  EDIT_ACCOUNT: 'edit-account',
  SEED_ACCOUNTS_CONFIRM: 'seed-accounts-confirm',

  // ── Accountant – Accounting Pages ────────────────────────────────
  CREATE_ACCOUNTING_PERIOD: 'create-accounting-period',
  CREATE_ANALYTIC_ACCOUNT: 'create-analytic-account',
  CREATE_COST_CENTER: 'create-cost-center',
  CREATE_CURRENCY: 'create-currency',
  CREATE_CURRENCY_RATE: 'create-currency-rate',
  CREATE_FISCAL_YEAR: 'create-fiscal-year',
  CREATE_FIXED_ASSET: 'create-fixed-asset',
  VOID_JOURNAL_ENTRY: 'void-journal-entry',
  CREATE_PAYMENT_TERM: 'create-payment-term',
  CREATE_PAYROLL_EMPLOYEE: 'create-payroll-employee',
  RUN_PAYROLL: 'run-payroll',
  CREATE_RECONCILIATION: 'create-reconciliation',
  UPDATE_RECONCILIATION: 'update-reconciliation',
  DELETE_RECONCILIATION: 'delete-reconciliation',
  CREATE_REVENUE_RECOGNITION: 'create-revenue-recognition',
  CREATE_TAX: 'create-tax',

  // ── Accountant – Finance Pages ────────────────────────────────────
  BANK_ACCOUNT_OPERATION: 'bank-account-operation',
  WALLET_OPERATION: 'wallet-operation',
  CREATE_BANK_ACCOUNT: 'create-bank-account',
  EDIT_BANK_ACCOUNT: 'edit-bank-account',
  CREATE_WALLET: 'create-wallet',
  EDIT_WALLET: 'edit-wallet',

  // ── Instructor / Mentor – Curriculum ──────────────────────────────
  TOPIC_DIALOG: 'topic-dialog',
  TITLE_DIALOG: 'title-dialog',

  // ── Instructor / Mentor – Group Details ───────────────────────────
  RESCHEDULE_SESSION: 'reschedule-session',
  SESSION_HISTORY: 'session-history',

  // ── Sales – Agents ────────────────────────────────────────────────
  ASSIGN_GROUPS: 'assign-groups',
  MANAGE_PREFERENCES: 'manage-preferences',

  // ── Sales – Commission ────────────────────────────────────────────
  CREATE_COMMISSION_RULE: 'create-commission-rule',
  EDIT_COMMISSION_RULE: 'edit-commission-rule',
  SET_COMMISSION: 'set-commission',
  AGENT_PROGRESS: 'agent-progress',

  // ── Sales – Distribution ──────────────────────────────────────────
  CONVERT_LEAD: 'convert-lead',
  VIEW_SUBMISSION: 'view-submission',

  // ── Sales – Assignments ───────────────────────────────────────────
  REASSIGN_LEAD: 'reassign-lead',
  CREATE_LEAD: 'create-lead',

  // ── Sales – Leads ─────────────────────────────────────────────────
  LOG_ACTION: 'log-action',

  // ── Sales – Analytics ─────────────────────────────────────────────
  SCORE_CONFIG: 'score-config',

  // ── Social Media – Forms ──────────────────────────────────────────
  CUSTOM_TEMPLATE: 'custom-template',
  FORM_TYPE: 'form-type',
  EXPORT_DISTRIBUTION: 'export-distribution',

  // ── Operation – Orders ────────────────────────────────────────────
  ORDER_ADD_PAYMENT: 'order-add-payment',
  ORDER_REFUND_BILL: 'order-refund-bill',
  ORDER_EDIT_BILL: 'order-edit-bill',
  EDIT_ORDER: 'edit-order',
  ACTION_ERROR: 'action-error',
} as const;

export type DialogId = (typeof DialogIds)[keyof typeof DialogIds] | string;
